import { FC, useCallback, useMemo } from 'react';
import { useDispatch, Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';

import alertTypes from 'constants/alertTypes';
import { store } from 'redux/store';
import { addAlert } from 'modules/_shared/redux/alertActions';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import SignatureBox from 'modules/_shared/components/SignatureBox';
import { useTranslate } from 'hooks/useTranslate';
import i18n from 'i18n/config';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import errorSwal from 'utils/errorSwal';
import {
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import {
  getIncentivesMarketPurchaseContractTemplateId,
  getIncentivesMarketTermsAndConditionsTemplateId,
} from 'modules/incentivesMarket/constants/incentivesMarketSocietyTemplates';
import IncentivesMarketTermsByDefault from 'modules/incentivesMarket/pages/IncentivesMarketTerms/components/IncentivesMarketTermsByDefault';
import IncentivesMarketTermsByTemplate from 'modules/incentivesMarket/pages/IncentivesMarketTerms/components/IncentivesMarketTermsByTemplate';
import IncentivesMarketPurchaseContractPreview from 'modules/incentivesMarket/components/IncentivesMarketPurchaseContractPreview';

import { distributeSellUnitsByPlan } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/distributeSellUnitsByPlan';
import { buildSellBreakdownFromSelection } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/buildSellBreakdownFromSelection';
import { mapSellerPlansToSellPlans } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/mapSellerPlansToSellPlans';
import { useAcceptIncentiveOfferModal } from '../context/AcceptIncentiveOfferModalContext';

const AcceptIncentiveOfferModalStep3: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { data, actions } = useAcceptIncentiveOfferModal();

  const isBuyOffer = data.offer.type === IncentiveOfferType.BUY;
  const confirmTitleKey = isBuyOffer
    ? 'IncentivesMarketConfirmSale'
    : 'IncentivesMarketConfirmPurchase';

  const publisherPartnerForPreview =
    useMemo((): Partial<IncentivesMarketBeneficiary> | null => {
      const p = data.offer.beneficiary;
      if (typeof p === 'object' && p !== null) {
        const nameFromOffer =
          typeof p.name === 'string' && p.name.trim().length > 0
            ? p.name
            : data.sellerName;
        return {
          ...p,
          name: nameFromOffer,
        };
      }
      const id = typeof p === 'string' ? p : undefined;
      if (!id && !data.sellerName) return null;
      return {
        _id: id,
        name: data.sellerName,
      };
    }, [data.offer.beneficiary, data.sellerName]);

  const publisherBeneficiaryId = useMemo(() => {
    const p = data.offer.beneficiary;
    if (typeof p === 'string') return p;
    return p?._id || '';
  }, [data.offer.beneficiary]);

  const strikePriceLabel = useMemo(() => {
    if (!data.sellerPlans.length) {
      return formatCurrencyDecimals(data.offer.unitPrice);
    }

    const rows = data.sellPlanSelection
      ? buildSellBreakdownFromSelection({
          plans: mapSellerPlansToSellPlans({
            offer: data.offer,
            sellerPlans: data.sellerPlans,
          }),
          sellerPlans: data.sellerPlans,
          unitsByPlan: data.sellPlanSelection,
        }).rows
      : distributeSellUnitsByPlan({
          plans: data.sellerPlans,
          totalUnits: data.purchaseQuantity,
          sellingPrice: data.offer.unitPrice,
        }).rows;

    const strikePrices = [
      ...new Set(
        rows.filter((row) => row.unitsSold > 0).map((row) => row.sharePrice)
      ),
    ];

    if (strikePrices.length === 0) {
      return formatCurrencyDecimals(data.offer.unitPrice);
    }

    if (strikePrices.length === 1) {
      return formatCurrencyDecimals(strikePrices[0]);
    }

    const minStrikePrice = Math.min(...strikePrices);
    const maxStrikePrice = Math.max(...strikePrices);

    return `${formatCurrencyDecimals(
      minStrikePrice
    )} - ${formatCurrencyDecimals(maxStrikePrice)}`;
  }, [
    data.offer,
    data.offer.unitPrice,
    data.purchaseQuantity,
    data.sellerPlans,
    data.sellPlanSelection,
  ]);

  const handleShowTerms = () => {
    const actualSociety = store.getState().society?.actualSociety;
    const templateId =
      getIncentivesMarketTermsAndConditionsTemplateId(actualSociety);

    dispatch(
      addAlert({
        type: alertTypes.NONE,
        title: `<h5 class="modal-title">${i18n.t(
          'OfferConditionsPageTitle'
        )}</h5>`,
        html: (
          <Provider store={store}>
            <I18nextProvider i18n={i18n}>
              {templateId ? (
                <IncentivesMarketTermsByTemplate templateId={templateId} />
              ) : (
                <IncentivesMarketTermsByDefault />
              )}
            </I18nextProvider>
          </Provider>
        ),
        props: {
          width: 'min(900px, 95vw)',
          confirmButtonText: i18n.t('Close'),
          onOpen: (popup: HTMLElement) => {
            const htmlContainer = popup.querySelector(
              '.swal2-html-container'
            ) as HTMLElement | null;
            if (htmlContainer) {
              htmlContainer.style.margin = '0';
              htmlContainer.style.padding = '0';
            }
          },
        },
      })
    );
  };

  const handleShowPurchaseContract = useCallback(
    (e: { preventDefault(): void }) => {
      e.preventDefault();
      const actualSociety = store.getState().society?.actualSociety;
      const templateId =
        getIncentivesMarketPurchaseContractTemplateId(actualSociety);
      const societyId = data.offer.society;

      if (!templateId || !societyId || !publisherBeneficiaryId) {
        errorSwal(t('ErrorNoIncentivesMarketTemplate'));
        return;
      }

      dispatch(
        addAlert({
          type: alertTypes.NONE,
          title: `<h5 class="modal-title">${t(
            'IncentivesMarketPurchaseContractPreviewTitle'
          )}</h5>`,
          html: (
            <Provider store={store}>
              <I18nextProvider i18n={i18n}>
                <IncentivesMarketPurchaseContractPreview
                  templateId={templateId}
                  societyId={societyId}
                  beneficiaryId={publisherBeneficiaryId}
                  offerType={data.offer.type}
                  previewVariant="accept"
                  language={i18n.language}
                  operationReferencePlaceholder={
                    data.offerReference ||
                    t('IncentivesMarketPurchaseContractDraftReference')
                  }
                  publisherPartner={publisherPartnerForPreview}
                  participantPartner={data.beneficiaryBuyer}
                  purchaseQuantity={data.purchaseQuantity}
                  unitPrice={data.offer.unitPrice}
                  totalPrice={data.totalPrice}
                  contractClassName={data.buyerPlansLabel}
                />
              </I18nextProvider>
            </Provider>
          ),
          props: {
            width: 'min(1200px, 95vw)',
            confirmButtonText: t('Close'),
            onOpen: (popup: HTMLElement) => {
              const htmlContainer = popup.querySelector(
                '.swal2-html-container'
              ) as HTMLElement | null;
              if (htmlContainer) {
                htmlContainer.style.margin = '0';
                htmlContainer.style.padding = '0';
              }
            },
          },
        })
      );
    },
    [
      data.offer.society,
      data.offer.type,
      data.offer.unitPrice,
      data.beneficiaryBuyer,
      data.purchaseQuantity,
      data.buyerPlansLabel,
      data.offerReference,
      data.totalPrice,
      dispatch,
      publisherBeneficiaryId,
      publisherPartnerForPreview,
      t,
    ]
  );

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-check-circle fs-24px text-info" />
        <h5 className="mt-2">{t(confirmTitleKey)}</h5>
        <p className="text-soft">{t('ReviewAndAcceptTermsToFinalize')}</p>
      </div>

      <div className="card card-bordered bg-info-dim mb-4">
        <div className="card-inner text-center">
          <h4 className="text-info mb-1">
            {formatCurrencyDecimals(data.totalPrice)}
          </h4>
          <p className="mb-0">
            {formatNumber(data.purchaseQuantity)}{' '}
            {t('IncentivesMarketUnits').toLowerCase()}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h6 className="text-center mb-3">{t('SignatureForContract')}</h6>
        <div className="d-flex justify-content-center">
          <SignatureBox
            isEmbedded
            singlePenColor
            className="w-100 mw-100"
            signature={data.participantSignature}
            setSignature={actions.setParticipantSignature}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2">
          <InputCheckbox
            id="accept-terms-checkbox"
            label={t('AcceptTermsAndConditionsOfOperation')}
            checked={data.isTermsAccepted}
            onChange={actions.handleToggleTerms}
            onLinkClick={handleShowTerms}
            linkLabel={t('ViewConditions')}
          />
        </div>
        <div className="custom-control custom-checkbox mb-2">
          <input
            type="checkbox"
            className="custom-control-input"
            id="accept-offer-purchase-contract-checkbox"
            checked={data.isPurchaseContractAccepted}
            onChange={actions.handleTogglePurchaseContract}
          />
          <label
            className="custom-control-label"
            htmlFor="accept-offer-purchase-contract-checkbox"
          >
            {t('AcceptOfferAcceptPurchaseContractIntro')}{' '}
            <button
              type="button"
              className="btn btn-link p-0 align-baseline"
              onClick={handleShowPurchaseContract}
            >
              {t('IncentivesMarketPurchaseContractShort')}
            </button>
            {t('AcceptOfferAcceptPurchaseContractOutro')}
          </label>
        </div>
        <div className="mb-2">
          <InputCheckbox
            id="accept-irrevocable-checkbox"
            label={t('UnderstandIrrevocableAcceptance')}
            checked={data.isIrrevocableAccepted}
            onChange={actions.handleToggleIrrevocable}
          />
        </div>
        <div>
          <InputCheckbox
            id="accept-fiscal-checkbox"
            label={t('IncentiveTaxWithholdingsDisclaimer')}
            checked={data.isFiscalAccepted}
            onChange={actions.handleToggleFiscal}
          />
        </div>
      </div>
    </div>
  );
};

export default AcceptIncentiveOfferModalStep3;
